import React from 'react';
import { COLORS } from '../data/constants'; // fixed: was '../data/constants' — adjust to match your folder structure

// ─────────────────────────────────────────────────────────────────────────────
//  SHARED UI COMPONENTS
//  Used across CustomerApp, ProfessionalApp, and AdminApp.
// ─────────────────────────────────────────────────────────────────────────────

// ─── BADGE ───────────────────────────────────────────────────────────────────
// Covers every status value that exists in the bookings + professionals tables.
// Original was missing: cancelled, verification_pending, rejected, suspended, more_docs
const STATUS_MAP = {
  // Booking statuses
  confirmed:              { bg:'#DBEAFE', color:'#1D4ED8', label:'Confirmed' },
  'in-progress':          { bg:'#FEF3C7', color:'#92400E', label:'In Progress' },
  pending:                { bg:'#F3F4F6', color:'#374151', label:'Pending' },
  completed:              { bg:'#D1FAE5', color:'#065F46', label:'Completed' },
  cancelled:              { bg:'#FEE2E2', color:'#DC2626', label:'Cancelled' },
  verification_pending:   { bg:'#EFF6FF', color:'#1D4ED8', label:'Verifying Payment' },

  // Professional / expert statuses
  available:              { bg:'#D1FAE5', color:'#065F46', label:'Available' },
  busy:                   { bg:'#FEF3C7', color:'#92400E', label:'Busy' },
  offline:                { bg:'#F3F4F6', color:'#374151', label:'Offline' },
  approved:               { bg:'#D1FAE5', color:'#065F46', label:'Approved' },
  rejected:               { bg:'#FEE2E2', color:'#DC2626', label:'Rejected' },
  suspended:              { bg:'#F1F5F9', color:'#475569', label:'Suspended' },
  under_review:           { bg:'#FFEDD5', color:'#EA580C', label:'Under Review' },
  more_docs:              { bg:'#EFF6FF', color:'#1D4ED8', label:'Docs Needed' },
};

export function Badge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <span style={{
      background: s.bg,
      color: s.color,
      borderRadius: 20,
      padding: '3px 10px',
      fontSize: 11,
      fontWeight: 700,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
}

// ─── BOTTOM NAV ──────────────────────────────────────────────────────────────
// paddingBottom uses env(safe-area-inset-bottom) so the nav clears the iPhone
// home indicator. Falls back to 8px on devices without a notch.
export function BottomNav({ tabs, active, onSelect }) {
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
      maxWidth: 430,
      background: COLORS.white,
      borderTop: `1px solid ${COLORS.grayLight || COLORS.border}`,
      display: 'flex',
      zIndex: 100,
      // Safe area for iPhone home bar — no overlap
      paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
      boxShadow: '0 -4px 24px rgba(0,0,0,.06)',
    }}>
      {tabs.map(t => {
        const on = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            style={{
              flex: 1,
              padding: '10px 4px 6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <div style={{
              width: 40,
              height: 30,
              borderRadius: 10,
              background: on ? (COLORS.primaryXL || '#E8EEF8') : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background .2s',
            }}>
              <span style={{ fontSize: 19 }}>{t.icon}</span>
            </div>
            <span style={{
              fontSize: 10,
              fontWeight: on ? 800 : 500,
              color: on ? (COLORS.primary || '#0F2D5C') : (COLORS.muted || COLORS.gray),
            }}>
              {t.label}
            </span>
            {on && (
              <div style={{
                width: 20,
                height: 3,
                background: COLORS.primary || '#0F2D5C',
                borderRadius: 2,
              }}/>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── LANG TOGGLE ─────────────────────────────────────────────────────────────
// Use this anywhere — TopBar now calls this internally instead of duplicating.
export function LangToggle({ lang, setLang }) {
  return (
    <button
      onClick={() => setLang(l => l === 'en' ? 'hi' : 'en')}
      style={{
        background: 'rgba(255,255,255,0.22)',
        border: '1px solid rgba(255,255,255,0.38)',
        borderRadius: 20,
        padding: '5px 13px',
        color: 'white',
        fontWeight: 700,
        fontSize: 12,
        cursor: 'pointer',
        fontFamily: 'inherit',
        flexShrink: 0,
      }}
    >
      {lang === 'en' ? 'हिंदी' : 'ENG'}
    </button>
  );
}

// ─── TOP BAR ─────────────────────────────────────────────────────────────────
// Uses LangToggle internally — no more duplicated toggle logic.
export function TopBar({ onBack, title, lang, setLang }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      background: COLORS.white,
      borderBottom: `1px solid ${COLORS.grayLight || COLORS.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      <button
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 20,
          padding: 0,
          color: COLORS.dark || COLORS.ink,
          lineHeight: 1,
          flexShrink: 0,
        }}
        aria-label="Go back"
      >
        ←
      </button>
      <span style={{
        fontWeight: 800,
        color: COLORS.dark || COLORS.ink,
        fontSize: 15,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {title}
      </span>
      {setLang && <LangToggle lang={lang} setLang={setLang}/>}
    </div>
  );
}

// ─── LOADING SPINNER ─────────────────────────────────────────────────────────
// Reusable full-screen or inline loading indicator.
export function LoadingSpinner({ fullScreen = false, message = 'Loading...' }) {
  const inner = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 36,
        height: 36,
        border: '3px solid #E8E8E8',
        borderTopColor: COLORS.primary || '#0F2D5C',
        borderRadius: '50%',
        animation: 'spin .7s linear infinite',
      }}/>
      {message && (
        <span style={{ fontSize: 13, color: COLORS.muted || COLORS.gray }}>{message}</span>
      )}
    </div>
  );

  if (!fullScreen) return inner;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: COLORS.bg || '#F4F6FA',
    }}>
      {inner}
    </div>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
// Standard empty state for lists — bookings, jobs, tickets, etc.
export function EmptyState({ icon = '📭', title, subtitle, action, actionLabel }) {
  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 18,
      padding: '40px 20px',
      textAlign: 'center',
      border: `1px solid ${COLORS.grayLight || COLORS.border}`,
    }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>{icon}</div>
      {title && (
        <div style={{ fontWeight: 700, color: COLORS.dark || COLORS.ink, fontSize: 15, marginBottom: 6 }}>
          {title}
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: 13, color: COLORS.muted || COLORS.gray, lineHeight: 1.6 }}>
          {subtitle}
        </div>
      )}
      {action && actionLabel && (
        <button
          onClick={action}
          style={{
            marginTop: 16,
            padding: '11px 28px',
            background: COLORS.primary || '#0F2D5C',
            border: 'none',
            borderRadius: 14,
            color: 'white',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
